import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Settings,
  Timer,
  Clock,
  Zap,
  ChevronUp,
  ChevronDown,
  Save,
  Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { QuickTools } from '../components/timer/QuickTools';
import { useFloatingTimer } from '../lib/useFloatingTimer';

// Preset timer configurations
const PRESET_TIMERS = [
  { name: 'HIIT Standard', work: 30, rest: 10, rounds: 8 },
  { name: 'Tabata', work: 20, rest: 10, rounds: 8 },
  { name: 'EMOM', work: 60, rest: 0, rounds: 10 },
  { name: 'Boxing Rounds', work: 180, rest: 60, rounds: 12 },
  { name: 'Quick Burn', work: 45, rest: 15, rounds: 6 },
  { name: 'Endurance', work: 60, rest: 30, rounds: 10 }
];

export const TimerPage = () => {
  // Timer settings
  const [workTime, setWorkTime] = useState(30);
  const [restTime, setRestTime] = useState(10);
  const [rounds, setRounds] = useState(8);
  const [prepTime, setPrepTime] = useState(5);
  
  // Timer state
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentRound, setCurrentRound] = useState(1);
  const [isWorkPhase, setIsWorkPhase] = useState(true);
  const [isPrep, setIsPrep] = useState(true);
  const [timeLeft, setTimeLeft] = useState(5);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  
  // Settings
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [savedTimers, setSavedTimers] = useState([]);
  
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Refs for timer callback to avoid stale closures
  const isPrepRef = useRef(isPrep);
  const isWorkPhaseRef = useRef(isWorkPhase);
  const currentRoundRef = useRef(currentRound);
  const roundsRef = useRef(rounds);
  const workTimeRef = useRef(workTime);
  const restTimeRef = useRef(restTime);
  
  useEffect(() => { isPrepRef.current = isPrep; }, [isPrep]);
  useEffect(() => { isWorkPhaseRef.current = isWorkPhase; }, [isWorkPhase]);
  useEffect(() => { currentRoundRef.current = currentRound; }, [currentRound]);
  const phaseTotal = (isPrep ? prepTime : isWorkPhase ? workTime : restTime) * 1000;
  useFloatingTimer(isRunning && !isComplete, {
    mode: 'countdown',
    label: isPrep ? 'Get ready' : `${isWorkPhase ? 'Work' : 'Rest'} · round ${currentRound}/${rounds}`,
    remainingMs: timeLeft * 1000, totalMs: phaseTotal, paused: isPaused,
    alert: !isPrep && isWorkPhase && currentRound >= rounds,
    onPause: () => pauseTimer(), onResume: () => resumeTimer(),
  });
  useEffect(() => { roundsRef.current = rounds; }, [rounds]);
  useEffect(() => { workTimeRef.current = workTime; }, [workTime]);
  useEffect(() => { restTimeRef.current = restTime; }, [restTime]);

  // Load saved timers from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('savedTimers');
    if (saved) {
      setSavedTimers(JSON.parse(saved));
    }
  }, []);

  // Play beep sound
  // Haptic feedback for phase transitions — works independently of
  // soundEnabled so users can mute audio in a loud gym and still feel
  // the next round kick in via `navigator.vibrate`. Safely no-ops on
  // iOS Safari where Web Vibration isn't supported.
  const vibrate = useCallback((pattern) => {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(pattern);
      }
    } catch {
      /* silent */
    }
  }, []);

  const playBeep = useCallback((frequency = 800, duration = 150, type = 'single', volume = 0.5) => {
    if (!soundEnabled) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      
      // Resume audio context if suspended (required for mobile browsers)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const playTone = (freq, dur, delay = 0, vol = volume) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'square'; // Square wave is louder and more like a boxing bell
        
        gainNode.gain.setValueAtTime(vol, ctx.currentTime + delay);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + dur / 1000);
        
        oscillator.start(ctx.currentTime + delay);
        oscillator.stop(ctx.currentTime + delay + dur / 1000);
      };
      
      if (type === 'triple') {
        // Workout complete - victory sound
        playTone(frequency, duration, 0, volume);
        playTone(frequency, duration, 0.2, volume);
        playTone(frequency * 1.5, duration * 2, 0.4, volume);
      } else if (type === 'double') {
        // Round start - two quick beeps
        playTone(frequency, duration, 0, volume);
        playTone(frequency, duration, 0.2, volume);
      } else if (type === 'countdown') {
        // Countdown beep - short tick
        playTone(frequency, duration, 0, volume);
      } else if (type === 'bell') {
        // Boxing bell sound - longer ring
        playTone(frequency, duration * 3, 0, volume * 1.2);
        playTone(frequency * 0.5, duration * 2, 0.1, volume * 0.8);
      } else {
        playTone(frequency, duration, 0, volume);
      }
    } catch (err) {
      // Audio playback failed (e.g. user gesture not yet granted) — stay silent
      if (typeof console !== 'undefined') console.debug('TimerPage sound silent:', err?.name || err);
    }
  }, [soundEnabled]);

  // Timer logic
  useEffect(() => {
    if (isRunning && !isPaused) {
      // Guard against double-intervals (e.g., React StrictMode double-invokes effects in dev)
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev === 3 || prev === 2) {
            playBeep(800, 150, 'single', 0.6);
          }
          if (prev === 1) {
            playBeep(1000, 200, 'single', 0.8);
          }
          
          if (prev <= 1) {
            const wt = workTimeRef.current;
            const rt = restTimeRef.current;
            const cr = currentRoundRef.current;
            const totalRounds = roundsRef.current;
            
            if (isPrepRef.current) {
              setIsPrep(false);
              setIsWorkPhase(true);
              playBeep(880, 300, 'bell', 0.9);
              vibrate([180]);
              return wt;
            } else if (isWorkPhaseRef.current) {
              if (rt > 0) {
                setIsWorkPhase(false);
                playBeep(660, 250, 'bell', 0.7);
                vibrate([90, 60, 90]);
                return rt;
              } else {
                if (cr >= totalRounds) {
                  setIsComplete(true);
                  setIsRunning(false);
                  playBeep(1200, 300, 'triple', 1.0);
                  vibrate([200, 80, 200, 80, 350]);
                  return 0;
                } else {
                  setCurrentRound(r => r + 1);
                  playBeep(880, 300, 'bell', 0.9);
                  vibrate([180]);
                  return wt;
                }
              }
            } else {
              if (cr >= totalRounds) {
                setIsComplete(true);
                setIsRunning(false);
                playBeep(1200, 300, 'triple', 1.0);
                vibrate([200, 80, 200, 80, 350]);
                return 0;
              } else {
                setCurrentRound(r => r + 1);
                setIsWorkPhase(true);
                playBeep(880, 300, 'bell', 0.9);
                vibrate([180]);
                return wt;
              }
            }
          }
          return prev - 1;
        });
        
        setTotalElapsed(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning, isPaused, playBeep, vibrate]);

  const startTimer = () => {
    if (isComplete) {
      resetTimer();
    }
    setIsRunning(true);
    setIsPaused(false);
    if (!isRunning) {
      setIsPrep(true);
      setTimeLeft(prepTime);
    }
    playBeep(880, 150, 'single', 0.6);
  };

  const pauseTimer = () => {
    setIsPaused(true);
    playBeep(440, 150, 'single', 0.5);
  };

  const resumeTimer = () => {
    setIsPaused(false);
    playBeep(880, 150, 'single', 0.6);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setIsPaused(false);
    setCurrentRound(1);
    setIsWorkPhase(true);
    setIsPrep(true);
    setTimeLeft(prepTime);
    setTotalElapsed(0);
    setIsComplete(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const adjustValue = (setter, current, delta, min, max) => {
    const newValue = current + delta;
    if (newValue >= min && newValue <= max) {
      setter(newValue);
    }
  };

  const loadPreset = (preset) => {
    setWorkTime(preset.work);
    setRestTime(preset.rest);
    setRounds(preset.rounds);
    resetTimer();
    toast.success(`Loaded ${preset.name} preset`);
  };

  const saveCurrentTimer = () => {
    const name = prompt('Enter a name for this timer:');
    if (name) {
      const newTimer = { name, work: workTime, rest: restTime, rounds };
      const updated = [...savedTimers, newTimer];
      setSavedTimers(updated);
      localStorage.setItem('savedTimers', JSON.stringify(updated));
      toast.success('Timer saved!');
    }
  };

  const deleteSavedTimer = (index) => {
    const updated = savedTimers.filter((_, i) => i !== index);
    setSavedTimers(updated);
    localStorage.setItem('savedTimers', JSON.stringify(updated));
    toast.success('Timer deleted');
  };

  const totalWorkoutTime = prepTime + (workTime + restTime) * rounds - restTime;
  const progress = totalElapsed / totalWorkoutTime * 100;

  return (
    <div className="min-h-screen bg-black" data-testid="timer-page">

      <main className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Timer Display */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-2xl p-4 md:p-6 mb-8"
          >
            {/* Progress Bar */}
            <div className="h-2 bg-black/50 mb-4 overflow-hidden">
              <motion.div
                className="h-full bg-primary"
                style={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Phase Indicator */}
            <div className="text-center mb-2">
              <span className={`font-heading text-xl uppercase tracking-widest ${
                isPrep ? 'text-blue-400' : isWorkPhase ? 'text-primary' : 'text-green-400'
              }`}>
                {isComplete ? 'Complete!' : isPrep ? 'Get Ready' : isWorkPhase ? 'Work' : 'Rest'}
              </span>
            </div>

            {/* Main Timer — kept big so gym-goers can read it from across the room. */}
            <div className="text-center mb-4">
              <motion.div
                key={timeLeft}
                initial={{ scale: 1.1, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`font-heading text-[104px] md:text-[152px] leading-none tracking-tight ${
                  isPrep ? 'text-blue-400' : isWorkPhase ? 'text-primary' : 'text-green-400'
                }`}
                data-testid="timer-display"
              >
                {formatTime(timeLeft)}
              </motion.div>
            </div>

            {/* Round Counter */}
            <div className="flex items-center justify-center gap-6 mb-5">
              <div className="text-center">
                <p className="text-muted-foreground text-xs uppercase tracking-wider">Round</p>
                <p className="font-heading text-3xl text-white">{currentRound}<span className="text-muted-foreground">/{rounds}</span></p>
              </div>
              <div className="h-10 w-px bg-border" />
              <div className="text-center">
                <p className="text-muted-foreground text-xs uppercase tracking-wider">Total Time</p>
                <p className="font-heading text-3xl text-white">{formatTime(totalElapsed)}</p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={resetTimer}
                variant="outline"
                size="lg"
                className="h-14 w-14 rounded-full border-border"
                data-testid="reset-timer-btn"
              >
                <RotateCcw className="h-5 w-5" />
              </Button>
              
              <Button
                onClick={isRunning && !isPaused ? pauseTimer : isRunning ? resumeTimer : startTimer}
                size="lg"
                className={`h-16 w-16 rounded-full ${
                  isRunning && !isPaused 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-primary hover:bg-primary/90'
                }`}
                data-testid="play-pause-btn"
              >
                {isRunning && !isPaused ? (
                  <Pause className="h-6 w-6 text-white" />
                ) : (
                  <Play className="h-6 w-6 text-black ml-0.5" />
                )}
              </Button>
              
              <Button
                onClick={() => setSoundEnabled(!soundEnabled)}
                variant="outline"
                size="lg"
                className="h-14 w-14 rounded-full border-border"
                data-testid="sound-toggle-btn"
              >
                {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
            </div>
          </motion.div>

          {/* Stopwatch + Countdown quick tools */}
          <QuickTools />

          {/* Settings Panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border rounded-2xl p-6 mb-8"
          >
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowSettings(!showSettings)}
            >
              <div className="flex items-center gap-3">
                <Settings className="h-5 w-5 text-primary" />
                <h3 className="font-heading text-xl text-white uppercase tracking-wider">Timer Settings</h3>
              </div>
              <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${showSettings ? 'rotate-180' : ''}`} />
            </div>

            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid md:grid-cols-4 gap-6 pt-6 border-t border-border mt-6">
                    {/* Work Time */}
                    <div className="space-y-2">
                      <Label className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                        <Zap className="h-4 w-4 text-primary" />
                        Work (sec)
                      </Label>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => adjustValue(setWorkTime, workTime, -5, 5, 600)}
                          disabled={isRunning}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          value={workTime}
                          onChange={(e) => setWorkTime(Math.max(5, Math.min(600, parseInt(e.target.value) || 5)))}
                          className="h-10 text-center bg-black border-border text-white"
                          disabled={isRunning}
                          data-testid="work-time-input"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => adjustValue(setWorkTime, workTime, 5, 5, 600)}
                          disabled={isRunning}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Rest Time */}
                    <div className="space-y-2">
                      <Label className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                        <Clock className="h-4 w-4 text-green-400" />
                        Rest (sec)
                      </Label>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => adjustValue(setRestTime, restTime, -5, 0, 300)}
                          disabled={isRunning}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          value={restTime}
                          onChange={(e) => setRestTime(Math.max(0, Math.min(300, parseInt(e.target.value) || 0)))}
                          className="h-10 text-center bg-black border-border text-white"
                          disabled={isRunning}
                          data-testid="rest-time-input"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => adjustValue(setRestTime, restTime, 5, 0, 300)}
                          disabled={isRunning}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Rounds */}
                    <div className="space-y-2">
                      <Label className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                        <Timer className="h-4 w-4 text-blue-400" />
                        Rounds
                      </Label>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => adjustValue(setRounds, rounds, -1, 1, 50)}
                          disabled={isRunning}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          value={rounds}
                          onChange={(e) => setRounds(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                          className="h-10 text-center bg-black border-border text-white"
                          disabled={isRunning}
                          data-testid="rounds-input"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => adjustValue(setRounds, rounds, 1, 1, 50)}
                          disabled={isRunning}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Prep Time */}
                    <div className="space-y-2">
                      <Label className="text-white uppercase tracking-wide text-sm flex items-center gap-2">
                        <Play className="h-4 w-4 text-purple-400" />
                        Prep (sec)
                      </Label>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => adjustValue(setPrepTime, prepTime, -1, 0, 30)}
                          disabled={isRunning}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          value={prepTime}
                          onChange={(e) => setPrepTime(Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
                          className="h-10 text-center bg-black border-border text-white"
                          disabled={isRunning}
                          data-testid="prep-time-input"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => adjustValue(setPrepTime, prepTime, 1, 0, 30)}
                          disabled={isRunning}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Total Time Display */}
                  <div className="mt-6 p-4 bg-black/50 border border-border rounded-xl">
                    <p className="text-center text-muted-foreground">
                      Total Workout Time: <span className="text-primary font-heading text-xl">{formatTime(totalWorkoutTime)}</span>
                    </p>
                  </div>

                  {/* Save Button */}
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={saveCurrentTimer}
                      variant="outline"
                      className="border-primary text-primary hover:bg-primary/10"
                      disabled={isRunning}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save This Timer
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Presets */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border rounded-2xl p-5"
          >
            <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4">Quick Presets</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {PRESET_TIMERS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => loadPreset(preset)}
                  disabled={isRunning}
                  className="px-4 py-3.5 bg-black/50 border border-border rounded-xl hover:border-primary/50 transition-colors text-center disabled:opacity-50"
                  data-testid={`preset-${preset.name.toLowerCase().replace(' ', '-')}`}
                >
                  <p className="font-heading text-white text-sm uppercase tracking-wider leading-tight mb-1">{preset.name}</p>
                  <p className="text-xs text-muted-foreground leading-tight">{preset.work}s / {preset.rest}s × {preset.rounds}</p>
                </button>
              ))}
            </div>

            {/* Saved Timers */}
            {savedTimers.length > 0 && (
              <div className="mt-5 pt-5 border-t border-border">
                <h4 className="font-heading text-base text-primary uppercase tracking-wider mb-3">Your Saved Timers</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {savedTimers.map((timer) => (
                    <div
                      key={`saved-${timer.name}-${timer.work}-${timer.rest}-${timer.rounds}`}
                      className="px-4 py-3.5 bg-primary/10 border border-primary/30 rounded-xl text-center relative group"
                    >
                      <button
                        onClick={() => loadPreset(timer)}
                        disabled={isRunning}
                        className="w-full disabled:opacity-50"
                      >
                        <p className="font-heading text-white text-sm uppercase tracking-wider leading-tight mb-1">{timer.name}</p>
                        <p className="text-xs text-muted-foreground leading-tight">{timer.work}s / {timer.rest}s × {timer.rounds}</p>
                      </button>
                      <button
                        onClick={() => deleteSavedTimer(savedTimers.indexOf(timer))}
                        className="absolute top-1.5 right-1.5 h-6 w-6 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};
